import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang, LANGS } from "../i18n";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  ["#home", "home"],
  ["#what-we-do", "what"],
  ["#services", "services"],
  ["#projects", "projects"],
  ["#about", "about"],
  ["#reviews", "reviews"],
  ["#socials", "socials"],
  ["#contacts", "contacts"],
];

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[100px] max-w-[1500px] items-center justify-between px-5 md:px-10">
          <button
            data-testid="logo-button"
            onClick={() => go("#home")}
            className="flex items-center"
          >
            <img src="/logo.png" alt="PROALP22" className="h-20 md:h-24 w-auto" />
          </button>

          <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
            {LINKS.map(([id, key]) => (
              <button
                key={id}
                data-testid={`nav-${key}`}
                onClick={() => go(id)}
                className="text-[13px] font-medium uppercase tracking-wider text-neutral-300 transition-colors hover:text-[#FF5A00]"
              >
                {t.nav[key]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div className="flex items-center gap-1" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-1.5 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                    lang === l ? "text-[#FF5A00]" : "text-neutral-500 hover:text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              data-testid="header-quote-btn"
              onClick={() => go("#contacts")}
              className="hidden md:flex items-center gap-2 bg-[#FF5A00] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04F00]"
            >
              {t.headerCta}
              <ArrowUpRight size={14} />
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              className="xl:hidden text-white"
              aria-label="Menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#050505]"
          >
            <div className="flex h-[100px] items-center justify-between px-5 border-b border-white/10">
              <img src="/logo.png" alt="PROALP22" className="h-16 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-6">
              {LINKS.map(([id, key], i) => (
                <motion.button
                  key={id}
                  data-testid={`mobile-nav-${key}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(id)}
                  className="border-b border-white/5 py-4 text-left font-display text-3xl uppercase tracking-wide text-neutral-200 transition-colors hover:text-[#FF5A00]"
                >
                  {t.nav[key]}
                </motion.button>
              ))}
            </nav>
            <div className="flex items-center justify-between px-6 pb-10">
              <div className="flex gap-3">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    data-testid={`mobile-lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`text-sm font-bold uppercase ${lang === l ? "text-[#FF5A00]" : "text-neutral-500"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button
                data-testid="mobile-quote-btn"
                onClick={() => go("#contacts")}
                className="bg-[#FF5A00] px-6 py-3 text-xs font-bold uppercase tracking-widest"
              >
                {t.headerCta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
